import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

/**
 * Routes a notification's action inside the site. It receives what the Notifications V2 action
 * router produces (`{ type, id | screen | url, ...params }`), already checked against the brand's
 * allowlists.
 *
 * A click can arrive before the router exists or before the session is restored (a click that
 * opened the site while logged out). It is held and replayed by <PushNotificationsRouter>, mounted
 * inside the <Router>, as soon as both are there.
 */

/**
 * The brand's screen registry (`GET /api/notifications_v2/config` → `screens`) names the app's
 * screens; these are the ones the site has. A screen the site lacks opens the site where it is.
 */
const SCREENS = {
  Home: '/',
  Promos: '/',
  MyAccount: '/mi-cuenta',
  MyPoints: '/mi-cuenta',
  Misiones: '/mi-cuenta/misiones',
  MyRedeems: '/mi-cuenta/cupones',
  Notifications: '/notificaciones',
  Profile: '/perfil',
};

const log = (...args) => console.log('[PushNotifications]', ...args);

let history = null;
let isSignedIn = false;
let pending = null;

/** 'A|B|C' navigates to C nested in B in A: the deepest screen the site has wins. */
export const pathForScreen = screen => {
  const hit = String(screen || '')
    .split('|')
    .reverse()
    .find(name => SCREENS[name] !== undefined);
  return hit === undefined ? null : SCREENS[hit];
};

const push = path => {
  if (history) {
    history.push(path);
  } else {
    window.location.assign(path);
  }
  return true;
};

const openUrl = value => {
  let url;
  try {
    url = new URL(value, window.location.origin);
  } catch {
    return false;
  }

  if (url.origin === window.location.origin) {
    return push(`${url.pathname}${url.search}${url.hash}`);
  }

  const opened = window.open(url.href, '_blank');
  if (opened) {
    opened.opener = null;
    return true;
  }
  log('the browser blocked the new window for', url.host);
  return false;
};

const navigate = data => {
  switch (data.type) {
    case 'promo':
      return data.id ? push(`/promo/${encodeURIComponent(data.id)}`) : false;
    case 'screen': {
      const path = pathForScreen(data.screen);
      return path === null ? false : push(path);
    }
    default:
      // `content` included: the site has no content screen.
      log('nothing to route for', data.type);
      return false;
  }
};

export const handlePushNotification = async data => {
  if (!data) {
    return false;
  }

  // Opening a link needs no router and no session, so it never has to wait.
  if (data.type === 'url') {
    return openUrl(data.url);
  }

  if (!history || !isSignedIn) {
    pending = data;
    return false;
  }

  pending = null;
  return navigate(data);
};

/** Replays a click that arrived before the router or the session. A no-op with nothing held. */
export const flushPendingNotification = () => {
  if (!pending) {
    return;
  }
  const data = pending;
  pending = null;
  handlePushNotification(data);
};

/** Gives the router to the push handlers and replays a held click once the session is there. */
export const PushNotificationsRouter = () => {
  const routerHistory = useHistory();
  const authenticated = useSelector(state => !!state.auth.isAuthenticated);

  useEffect(() => {
    history = routerHistory;
    flushPendingNotification();
    return () => {
      if (history === routerHistory) {
        history = null;
      }
    };
  }, [routerHistory]);

  useEffect(() => {
    isSignedIn = authenticated;
    if (authenticated) {
      flushPendingNotification();
    }
  }, [authenticated]);

  return null;
};

export default PushNotificationsRouter;
